/* eslint-disable no-console */
import { useReducer, useEffect } from 'react';
import moment from 'moment';
import axios from 'supports/api';
import {
  createInitialForm,
  INT_FIELDS,
  ALWAYS_VALID_FIELDS,
} from './constants';

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return {
        ...state,
        [action.name]: { ...state[action.name], ...action.patch },
      };
    case 'SET_FIELDS': {
      const next = { ...state };
      Object.entries(action.patches).forEach(([name, patch]) => {
        next[name] = { ...next[name], ...patch };
      });
      return next;
    }
    case 'MARK_INVALID': {
      const next = { ...state };
      action.names.forEach((name) => {
        next[name] = { ...next[name], invalid: true };
      });
      return next;
    }
    case 'RESET':
      return createInitialForm();
    default:
      return state;
  }
};

// Form-field state, keyed by field name; every field keeps the legacy
// { value, valid, invalid, error } shape consumed by InputForm/HTMLForm.
const useScheduleForm = ({ edit, programId }) => {
  const [fields, dispatch] = useReducer(reducer, undefined, createInitialForm);

  const setField = (name, patch) =>
    dispatch({ type: 'SET_FIELD', name, patch });
  const setFields = (patches) => dispatch({ type: 'SET_FIELDS', patches });
  const markInvalid = (names) => dispatch({ type: 'MARK_INVALID', names });
  const resetForm = () => dispatch({ type: 'RESET' });

  const handleInput = (name, value) => {
    const parsedValue =
      INT_FIELDS.includes(name) && value ? parseInt(value, 10) : value;
    const valid = Boolean(value) || ALWAYS_VALID_FIELDS.includes(name);
    setField(name, { value: parsedValue, valid, invalid: !valid });
  };

  // used by HTMLEditor2 for array-of-items fields (syllabus/audience)
  const handleItemInput = (name, index, value, prop) => {
    const items = fields[name].value.map((item, i) =>
      i === index ? { ...item, [prop]: value } : item,
    );
    setField(name, { value: items });
  };

  const handleSelect = (name, value) => {
    const valid = value !== 0;
    setField(name, { value, valid, invalid: !valid });
  };

  const handleOption = (name, value) => {
    const valid = value !== '';
    setField(name, { value, valid, invalid: !valid });
  };

  const handleCurrencyInput = (name, value) => {
    let number = parseInt(value.split(',').join(''), 10);
    let displayValue = number.toLocaleString();

    if (value.split(',').join('') === '') {
      number = 0;
      displayValue = '0';
    }
    setField(name, { value: number, displayValue });
  };

  const addItem = (name) => {
    setField(name, { value: [...fields[name].value, { item: '' }] });
  };

  const removeItem = (name) => {
    setField(name, { value: fields[name].value.slice(0, -1) });
  };

  const handleStartEndDate = (startDate, endDate, startTime, endTime) => {
    const defaultTime = null;

    const startTimeValue = startTime || fields.startTime?.value || defaultTime;
    const endTimeValue = endTime || fields.endTime?.value || defaultTime;

    const adjustedStartDate = moment(startDate)
      .set({
        hour: moment(startTimeValue, 'HH:mm').hour(),
        minute: moment(startTimeValue, 'HH:mm').minute(),
        second: 0,
      })
      .toDate();

    const adjustedEndDate = moment(endDate)
      .set({
        hour: moment(endTimeValue, 'HH:mm').hour(),
        minute: moment(endTimeValue, 'HH:mm').minute(),
        second: 0,
      })
      .toDate();

    if (adjustedStartDate > adjustedEndDate) {
      setFields({
        startDate: { value: adjustedEndDate },
        endDate: { value: adjustedStartDate },
      });
    } else {
      setFields({
        startDate: { value: adjustedStartDate },
        endDate: { value: adjustedEndDate },
      });
    }
  };

  const handleStartTime = (value) => {
    const newStartTime = value || null;
    const adjustedStartDate = moment(fields.startDate.value)
      .set({
        hour: moment(newStartTime, 'HH:mm').hour(),
        minute: moment(newStartTime, 'HH:mm').minute(),
        second: 0,
      })
      .toDate();

    setFields({
      startTime: { value: newStartTime },
      startDate: { value: adjustedStartDate },
    });
  };

  const handleEndTime = (value) => {
    const newEndTime = value || null;
    const adjustedEndDate = moment(fields.endDate.value)
      .set({
        hour: moment(newEndTime, 'HH:mm').hour(),
        minute: moment(newEndTime, 'HH:mm').minute(),
        second: 0,
      })
      .toDate();

    setFields({
      endTime: { value: newEndTime },
      endDate: { value: adjustedEndDate },
    });
  };

  const handlePracticeStartTime = (value) => {
    setField('practiceStartTime', { value });
  };

  const handlePracticeEndTime = (value) => {
    setField('practiceEndTime', { value });
  };

  // edit mode: hydrate every field from the existing schedule
  useEffect(() => {
    if (!edit) return;

    axios
      .get(`/admin/program/schedule/${programId}`)
      .then((res) => {
        const { result } = res.data;
        const {
          branchId,
          study_methodId,
          dayType,
          regionType,
          study_scheduleId,
          cityId,
          venueId,
          code,
          days,
          startDate,
          endDate,
          specialStartDate,
          specialEndDate,
          earlyStartDate,
          earlyEndDate,
          termStartDate,
          termEndDate,
          term2StartDate,
          term2EndDate,
          normalPromoStartDate,
          normalPromoEndDate,
          corporateSaleStartDate,
          corporateSaleEndDate,
          saleEndDate,
          startTime,
          endTime,
          practiceStartTime,
          practiceEndTime,
          normalPrice,
          specialPrice,
          earlyPrice,
          termPrice,
          term2Price,
          normalPromoPrice,
          corporatePrice,
          registrationFee,
          maxSeats,
          speakers,
          workshopDetail,
          program_prices,
          programAdditionalPromos,
          specialDesc,
          specialQuota,
          specialInfo,
          practiceDays,
          priceTitle,
          priceTitleDesc,
          priceQuota,
          priceQuotaRemaining,
          priceInfo,
        } = result;

        // absent optional dates become '' except the corporate pair (null),
        // matching what the legacy form always did
        const optionalDate = (value) => ({
          value: value ? new Date(value) : '',
          valid: Boolean(value),
        });

        dispatch({
          type: 'SET_FIELDS',
          patches: {
            branchId: { value: branchId, valid: true },
            study_methodId: { value: study_methodId, valid: true },
            study_scheduleId: { value: study_scheduleId, valid: true },
            cityId: { value: cityId, valid: true },
            venueId: { value: venueId, valid: true },
            dayType: { value: dayType, valid: true },
            days: { value: days, valid: true },
            startDate: { value: new Date(startDate), valid: true },
            endDate: { value: new Date(endDate), valid: true },
            specialStartDate: optionalDate(specialStartDate),
            specialEndDate: optionalDate(specialEndDate),
            earlyStartDate: optionalDate(earlyStartDate),
            earlyEndDate: optionalDate(earlyEndDate),
            termStartDate: optionalDate(termStartDate),
            termEndDate: optionalDate(termEndDate),
            term2StartDate: optionalDate(term2StartDate),
            term2EndDate: optionalDate(term2EndDate),
            normalPromoStartDate: optionalDate(normalPromoStartDate),
            normalPromoEndDate: optionalDate(normalPromoEndDate),
            corporateSaleStartDate: {
              value: corporateSaleStartDate
                ? new Date(corporateSaleStartDate)
                : null,
              valid: Boolean(corporateSaleStartDate),
            },
            corporateSaleEndDate: {
              value: corporateSaleEndDate
                ? new Date(corporateSaleEndDate)
                : null,
              valid: Boolean(corporateSaleEndDate),
            },
            saleEndDate: optionalDate(saleEndDate),
            startTime: { value: startTime, valid: true },
            endTime: { value: endTime, valid: true },
            practiceStartTime: { value: practiceStartTime, valid: true },
            practiceEndTime: { value: practiceEndTime, valid: true },
            price: {
              value: normalPrice,
              displayValue: (normalPrice ?? 0).toLocaleString(),
              valid: true,
            },
            specialPrice: {
              value: specialPrice,
              displayValue: (specialPrice || 0).toLocaleString(),
              valid: true,
            },
            earlyPrice: {
              value: earlyPrice,
              displayValue: (earlyPrice ?? 0).toLocaleString(),
              valid: true,
            },
            termPrice: {
              value: termPrice,
              displayValue: (termPrice || 0).toLocaleString(),
              valid: true,
            },
            term2Price: {
              value: term2Price,
              displayValue: (term2Price || 0).toLocaleString(),
              valid: true,
            },
            normalPromoPrice: {
              value: normalPromoPrice,
              displayValue: (normalPromoPrice || 0).toLocaleString(),
              valid: true,
            },
            corporatePrice: {
              value: corporatePrice,
              displayValue: (corporatePrice || 0).toLocaleString(),
              valid: true,
            },
            maxSeats: { value: maxSeats, valid: true },
            registrationFee: {
              value: registrationFee,
              displayValue: (registrationFee ?? 0).toLocaleString(),
              valid: true,
            },
            speakerIds: { value: speakers, valid: true },
            code: code ? { value: code, valid: true } : { valid: false },
            programPrices: { value: program_prices || [] },
            programAdditionalPromos: {
              value: programAdditionalPromos || [],
            },
            specialDesc: { value: specialDesc, valid: true },
            specialQuota: { value: specialQuota ?? 0, valid: true },
            specialInfo: { value: specialInfo, valid: true },
            practiceDays: { value: practiceDays, valid: true },
            priceTitle: { value: priceTitle, valid: true },
            priceTitleDesc: { value: priceTitleDesc, valid: true },
            priceQuota: { value: priceQuota ?? 0, valid: true },
            priceQuotaRemaining: {
              value: priceQuotaRemaining ?? 0,
              valid: true,
            },
            priceInfo: { value: priceInfo, valid: true },
          },
        });

        if (workshopDetail) {
          const { content, syllabus, audience, notes } = workshopDetail;
          const parsedSyllabus = JSON.parse(syllabus);
          const parsedAudience = JSON.parse(audience);
          dispatch({
            type: 'SET_FIELDS',
            patches: {
              regionType: { value: regionType, valid: !!regionType },
              content: content
                ? { value: content, valid: true }
                : { valid: false },
              syllabus: parsedSyllabus
                ? { value: parsedSyllabus, valid: !!syllabus }
                : { valid: !!syllabus },
              audience: parsedAudience
                ? { value: parsedAudience, valid: !!audience }
                : { valid: !!audience },
              notes: notes ? { value: notes, valid: true } : { valid: true },
            },
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, programId]);

  return {
    fields,
    setField,
    setFields,
    markInvalid,
    resetForm,
    handleInput,
    handleItemInput,
    handleSelect,
    handleOption,
    handleCurrencyInput,
    addItem,
    removeItem,
    handleStartEndDate,
    handleStartTime,
    handleEndTime,
    handlePracticeStartTime,
    handlePracticeEndTime,
  };
};

export default useScheduleForm;
